import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Badge } from '@/components/atoms/Badge';
import { cardSurface } from '@/components/atoms/Card';
import type { OverviewCage } from '@/domain/fishGroups';

import type { OperateWizardAction, OperationKind } from '../operateWizardReducer';

import styles from '../operateModals.module.scss';

interface Props {
    locationCages: OverviewCage[];
    initialFishGroupId: number;
    currentKind: OperationKind | null;
    dispatch: Dispatch<OperateWizardAction>;
}

interface KindViability {
    available: boolean;
    /** i18n key for the tooltip when the mode is unavailable. */
    unavailableReasonKey?: string;
}

/**
 * Step 0 — the operator picks which kind of operation they are about to
 * execute. Each mode is exposed as a card with its own copy and viability
 * check (disabled with a tooltip when the current location can't satisfy
 * the mode's preconditions).
 *
 * The intent is to make the operation type a conscious, explicit choice
 * — never inferred from form shape later — so the operator sees the
 * consequences of their click up front.
 */
export default function OperationKindStep(props: Props) {
    const { locationCages, initialFishGroupId, currentKind, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    const emptyCages = locationCages.filter((c) => c.activeFishGroup === null).length;
    const otherActiveFGs = locationCages.filter(
        (c) => c.activeFishGroup !== null && c.activeFishGroup.id !== initialFishGroupId
    ).length;

    const viability: Record<OperationKind, KindViability> = {
        split:
            emptyCages >= 1
                ? { available: true }
                : {
                      available: false,
                      unavailableReasonKey: 'actions.operate.kind.disabled.noEmptyCage',
                  },
        merge:
            otherActiveFGs >= 1
                ? { available: true }
                : {
                      available: false,
                      unavailableReasonKey: 'actions.operate.kind.disabled.noOtherFGs',
                  },
        split_and_merge:
            // The continuation now lives in the source's own cage, so we no
            // longer require an empty cage — only another active FG to act
            // as the merge target.
            otherActiveFGs >= 1
                ? { available: true }
                : {
                      available: false,
                      unavailableReasonKey: 'actions.operate.kind.disabled.noOtherFGs',
                  },
        redistribution:
            otherActiveFGs >= 1
                ? { available: true }
                : {
                      available: false,
                      unavailableReasonKey: 'actions.operate.kind.disabled.noOtherFGs',
                  },
    };

    const cards: { kind: OperationKind; advanced?: boolean }[] = [
        { kind: 'split' },
        { kind: 'merge' },
        { kind: 'split_and_merge' },
        { kind: 'redistribution', advanced: true },
    ];

    return (
        <section className={styles.kindStep}>
            <h4 className={styles.heading}>{t('actions.operate.kind.heading')}</h4>
            <p className={styles.description}>{t('actions.operate.kind.description')}</p>
            <div className={styles.kindCards}>
                {cards.map(({ kind, advanced }) => {
                    const { available, unavailableReasonKey } = viability[kind];
                    const isSelected = currentKind === kind;
                    return (
                        <button
                            key={kind}
                            type="button"
                            {...cardSurface(
                                {
                                    radius: 'sm',
                                    padding: 'xs',
                                    interactive: true,
                                    selected: isSelected,
                                },
                                classNames(styles.kindCard, {
                                    [styles['kindCard--disabled']]: !available,
                                })
                            )}
                            disabled={!available}
                            title={
                                available
                                    ? undefined
                                    : unavailableReasonKey
                                      ? t(unavailableReasonKey)
                                      : undefined
                            }
                            onClick={() => dispatch({ type: 'setOperationKind', kind })}>
                            <div className={styles.kindCardHeader}>
                                <h5 className={styles.kindCardTitle}>
                                    {t(`actions.operate.kind.cards.${kind}.title`)}
                                </h5>
                                {advanced && (
                                    <Badge size="xs" shape="tag">
                                        {t('actions.operate.kind.advancedBadge')}
                                    </Badge>
                                )}
                            </div>
                            <p className={styles.kindCardDescription}>
                                {t(`actions.operate.kind.cards.${kind}.description`)}
                            </p>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
